import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Products.css';
import ProductCard from './ProductCard';
import { API_BASE } from '../config/api';
import LoadingSpinner from './LoadingSpinner';

const Products = ({ selectedCategory, selectedSubcategory, onAddToCart }) => {
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categoryTitle, setCategoryTitle] = useState('New Arrivals');
  const [loading, setLoading] = useState(true);
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    // Check if we're in search mode
    const urlParams = new URLSearchParams(location.search);
    const isSearchMode = urlParams.get('search') === 'true';
    setSearchMode(isSearchMode);
    
    if (!isSearchMode) {
      fetchProducts();
    } else {
      setCategoryTitle('Search Products');
      setFilteredProducts([]);
      setLoading(false);
    }
  }, [selectedCategory, selectedSubcategory, location.search]);

  useEffect(() => {
    if (searchMode && searchQuery.length >= 3) {
      const delaySearch = setTimeout(() => {
        searchProducts();
      }, 300); // Debounce search
      
      return () => clearTimeout(delaySearch);
    } else if (searchMode && searchQuery.length < 3) {
      setFilteredProducts([]);
    }
  }, [searchQuery, searchMode]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('isActive', 'true');
      params.append('limit', '100');
      
      // Check for showcaseSection from URL query params
      const urlParams = new URLSearchParams(location.search);
      const showcaseSection = urlParams.get('showcaseSection');
      const categoryParam = urlParams.get('category');
      const subcategoriesParam = urlParams.get('subcategories');
      
      if (showcaseSection) {
        params.append('showcaseSection', showcaseSection);
      } else if (categoryParam) {
        // Use category from URL instead of props
        params.append('category', categoryParam);
        
        // Handle multiple subcategories if provided
        if (subcategoriesParam) {
          // For multiple subcategories, we'll fetch and filter client-side
          // since the backend subcategory param only handles one at a time
        }
      } else {
        // Fallback to props only if no URL params
        if (selectedCategory && selectedCategory !== 'all') {
          params.append('category', selectedCategory);
        }
        
        if (selectedSubcategory && selectedSubcategory !== 'all') {
          params.append('subcategory', selectedSubcategory);
        }
      }

      const response = await fetch(`${API_BASE}/products?${params.toString()}`);
      const data = await response.json();
      
      console.log('Products Fetch - URL:', `${API_BASE}/products?${params.toString()}`);
      console.log('Products Fetch - Response:', data);
      console.log('Products Fetch - Found products:', data.data?.length || 0);
      
      if (data.success) {
        let products = data.data || [];
        
        // Filter by subcategories if multiple were specified
        if (subcategoriesParam) {
          const subcategories = subcategoriesParam.split(',');
          products = products.filter(product => 
            product.subcategories && 
            product.subcategories.some(sub => subcategories.includes(sub))
          );
          console.log('After subcategory filter:', products.length);
        }
        
        setFilteredProducts(products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
    updateTitle();
  };

  const searchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('isActive', 'true');
      params.append('search', searchQuery);
      params.append('limit', '100');

      const response = await fetch(`${API_BASE}/products?${params.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        setFilteredProducts(data.data || []);
      }
    } catch (error) {
      console.error('Error searching products:', error);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const updateTitle = () => {
    // Check for showcaseSection from URL
    const urlParams = new URLSearchParams(location.search);
    const showcaseSection = urlParams.get('showcaseSection');
    const categoryParam = urlParams.get('category');
    const categoryName = urlParams.get('categoryName');
    
    if (showcaseSection) {
      const showcaseTitles = {
        'new-arrivals': 'New Arrivals',
        'trending-now': 'Trending Now',
        'mega-genie-deals': 'Mega Genie Deals',
        'bestsellers': 'Bestsellers',
        'clearance-sale': 'Clearance Sale'
      };
      setCategoryTitle(showcaseTitles[showcaseSection] || 'Products');
      return;
    }
    
    // Check for category from URL params (when clicked from category card)
    if (categoryName) {
      setCategoryTitle(decodeURIComponent(categoryName));
      return;
    }
    
    if (categoryParam) {
      const categoryNames = {
        'women': "Women's Collection",
        'men': "Men's Collection",
        'kids': "Kids Collection",
        'bags': "Bags & Luggage",
        'beauty': "Beauty & Personal Care",
        'electronics': "Electronics & Gadgets",
        'home': "Home & Living",
        'sports': "Sports & Outdoors",
        'unisex': "Unisex Collection"
      };
      setCategoryTitle(categoryNames[categoryParam] || categoryParam.charAt(0).toUpperCase() + categoryParam.slice(1));
      return;
    }
    
    const titles = {
      'Women': {
        'Tops & T-Shirts': "Women's Tops & T-Shirts",
        'Dresses': "Women's Dresses",
        'Jeans & Trousers': "Women's Jeans & Trousers",
        'Skirts & Shorts': "Women's Skirts & Shorts",
        'Sweaters & Jackets': "Women's Sweaters & Jackets",
        'Lingerie & Sleepwear': "Women's Lingerie & Sleepwear",
        'Heels': "Women's Heels",
        'Flats': "Women's Flats",
        'Sandals': "Women's Sandals",
        'Sneakers': "Women's Sneakers",
        'Boots': "Women's Boots",
        'Jewelry': "Women's Jewelry",
        'Bags & Purses': "Women's Bags & Purses",
        'Watches': "Women's Watches",
        'Sunglasses': "Women's Sunglasses",
        'Scarves & Hats': "Women's Scarves & Hats",
        'Hair Accessories': "Women's Hair Accessories"
      },
      'Men': {
        'T-Shirts & Polos': "Men's T-Shirts & Polos",
        'Shirts': "Men's Shirts",
        'Jeans': "Men's Jeans",
        'Trousers & Shorts': "Men's Trousers & Shorts",
        'Jackets & Coats': "Men's Jackets & Coats",
        'Sweatshirts & Hoodies': "Men's Sweatshirts & Hoodies",
        'Innerwear & Sleepwear': "Men's Innerwear & Sleepwear",
        'Casual Shoes': "Men's Casual Shoes",
        'Formal Shoes': "Men's Formal Shoes",
        'Sneakers': "Men's Sneakers",
        'Sandals & Slides': "Men's Sandals & Slides",
        'Boots': "Men's Boots",
        'Watches': "Men's Watches",
        'Wallets': "Men's Wallets",
        'Belts': "Men's Belts",
        'Sunglasses': "Men's Sunglasses",
        'Caps & Hats': "Men's Caps & Hats",
        'Socks': "Men's Socks"
      },
      'Kids': {
        'Boys\' Clothing': "Boys' Clothing",
        'Boys\' Footwear': "Boys' Footwear",
        'Girls\' Clothing': "Girls' Clothing",
        'Girls\' Footwear': "Girls' Footwear",
        'Toys & Games': "Toys & Games",
        'Baby Essentials': "Baby Essentials"
      },
      'Bags & Luggage': {
        'Backpacks': "Backpacks",
        'Handbags': "Handbags",
        'Sling Bags': "Sling Bags",
        'Laptop Bags': "Laptop Bags",
        'Trolley Bags': "Trolley Bags",
        'Wallets & Clutches': "Wallets & Clutches"
      },
      'Beauty & Personal Care': {
        'Skincare': "Skincare",
        'Haircare': "Haircare",
        'Makeup': "Makeup",
        'Fragrances': "Fragrances",
        'Bath & Body': "Bath & Body"
      },
      'Home & Living': {
        'Home Décor': "Home Décor",
        'Kitchen & Dining': "Kitchen & Dining",
        'Lighting': "Lighting"
      },
      'Sports & Outdoors': {
        'Fitness Equipment': "Fitness Equipment",
        'Sportswear': "Sportswear"
      }
    };

    if (selectedCategory && selectedSubcategory && titles[selectedCategory]?.[selectedSubcategory]) {
      setCategoryTitle(titles[selectedCategory][selectedSubcategory]);
    } else if (selectedCategory === 'Women') {
      setCategoryTitle("Women's Collection");
    } else if (selectedCategory === 'Men') {
      setCategoryTitle("Men's Collection");
    } else if (selectedCategory === 'Kids') {
      setCategoryTitle("Kids Collection");
    } else if (selectedCategory === 'Bags & Luggage') {
      setCategoryTitle("Bags & Luggage");
    } else if (selectedCategory === 'Beauty & Personal Care') {
      setCategoryTitle("Beauty & Personal Care");
    } else if (selectedCategory === 'Electronics & Gadgets') {
      setCategoryTitle("Electronics & Gadgets");
    } else if (selectedCategory === 'Home & Living') {
      setCategoryTitle("Home & Living");
    } else if (selectedCategory === 'Sports & Outdoors') {
      setCategoryTitle("Sports & Outdoors");
    } else {
      setCategoryTitle("All Products");
    }
  };

  return (
    <section className="products" id="product-section">
      <div className="products-container">
        <div className="products-header">
          {searchMode ? (
            <div className="search-input-container">
              <input
                type="text"
                className="search-input"
                placeholder="Search for products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </div>
          ) : (
            <h2 className="products-title">{categoryTitle}</h2>
          )}
          <div className="products-count">{filteredProducts.length} items</div>
        </div>
        
        {loading ? (
          <LoadingSpinner />
        ) : filteredProducts.length > 0 ? (
          <div className="products-layout">
            {/* Product Grid */}
            <div className="product-grid">
              {filteredProducts.map((product, index) => (
                <ProductCard
                  key={product._id || product.id}
                  product={product}
                  onAddToCart={onAddToCart}
                  featured={index === 0}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="no-products">No products found in this category.</p>
        )}
      </div>
    </section>
  );
};

export default Products;
